import React, {useCallback, useEffect, useImperativeHandle, useState, forwardRef} from 'react';
import DroppableList from '../DraggingWrappers/DroppableList';
import { RelatedPattern } from '../../RelatedPattern/RelatedPattern';
import styles from "../Merging.module.css";
import PulsingDivWrapper from "../DraggingWrappers/PulsingDivWrapper";

const RelatedPatternMergingDisplay = forwardRef( ({ patternSelected, fromPattern, toPattern }, ref) => {
    const [fromList, setFromList] = useState([]);
    const [toList, setToList] = useState([]);
    const [draggingTo, setDraggingTo] = useState(false)
    const [draggingFrom, setDraggingFrom] = useState(false)

    const existsInToList = useCallback((item) => {
        return (
            Array.isArray(toList) &&
            item && item.relatedPat &&
            toList.some(f => +item.relatedPat.related_pattern_id === +f.relatedPat.related_pattern_id)
        );
    }, [toList]);

    const existsInFromList = useCallback((item) => {
        return (
            Array.isArray(fromList) &&
            item && item.relatedPat &&
            fromList.some(f => +item.relatedPat.related_pattern_id === +f.relatedPat.related_pattern_id)
        );
    }, [fromList]);

    const handleUpdateRelatedPattern = (updatedPattern) => {
        // Update toList
        setToList((prevToList) =>
            prevToList.map((item) => {
                if (+item.relatedPat.related_pattern_id === +updatedPattern.id) {
                    return {
                        ...item,
                        relatedPat: {...item.relatedPat, relationship_description: updatedPattern.description},
                        content: (
                            <RelatedPattern
                                key={`RelatedPattern-${item.relatedPat.related_pattern_id}`}
                                patternSelected={patternSelected}
                                related_pattern={{...item.relatedPat, relationship_description: updatedPattern.description}}
                                update={handleUpdateRelatedPattern}
                                isMerged
                                disableClick
                                fromStyles={!item.disableDrag}
                                liveEdit
                            />
                        ),
                    };
                }
                return item;
            })
        );
    };

    useEffect(() => {
        let isMounted = true;

        if (fromPattern && fromPattern.related_patterns) {
            const updatedList = fromPattern.related_patterns.map((relatedPat, index) => {
                const id = relatedPat.related_pattern_id + " relationship-" + relatedPat.pattern_relationship;
                const isGrayedOut = existsInToList({ relatedPat: relatedPat });
                return {
                    id,
                    relatedPat,
                    content: (
                        <div className={isGrayedOut ? styles.GrayedOut : ''}>
                            <RelatedPattern
                                key={"RelatedPattern-" + id}
                                update={handleUpdateRelatedPattern}
                                patternSelected={patternSelected}
                                related_pattern={relatedPat}
                                disableClick
                                fromStyles={!isGrayedOut}
                                liveEdit
                            />
                        </div>
                    ),
                    index,
                    disableDrag: isGrayedOut,
                };
            });

            if (isMounted) setFromList(updatedList);
        }

        return () => {
            isMounted = false; // Cleanup on unmount
        };
    }, [existsInToList, fromPattern, patternSelected]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        let isMounted = true; // Track component's mounted state

        if (toPattern && toPattern.related_patterns) {
            const updatedList = toPattern.related_patterns.map((relatedPat, index) => {
                const id = relatedPat.related_pattern_id + " relationship-" + relatedPat.pattern_relationship;
                return {
                    id,
                    relatedPat,
                    content: (
                        <RelatedPattern
                            key={"RelatedPattern-" + id}
                            update={handleUpdateRelatedPattern}
                            patternSelected={patternSelected}
                            related_pattern={relatedPat}
                            isMerged={true}
                            disableClick
                            liveEdit
                        />
                    ),
                    index,
                    disableDrag: true,
                };
            });

            if (isMounted) setToList(updatedList);
        }

        // Cleanup function to mark the component as unmounted
        return () => {
            isMounted = false;
        };
    }, [toPattern, patternSelected]); // eslint-disable-line react-hooks/exhaustive-deps


    const moveItemBetweenLists = useCallback((item, start, destination) => {
        let isMounted = true;

        if (start === destination) return;

        if (start === 'fromList' && !existsInToList(item)) {
            let newItem = { ...item };
            newItem.content = (
                <RelatedPattern
                    key={`RelatedPattern-${item.relatedPat.related_pattern_id}`}
                    patternSelected={patternSelected}
                    related_pattern={item.relatedPat}
                    update={handleUpdateRelatedPattern}
                    isMerged={true}
                    disableClick
                    fromStyles={true}
                    liveEdit
                />
            );

            if (isMounted) setToList((prev) => [newItem, ...prev]); // Add to target
        } else if (start === 'toList' && destination === 'fromList' && existsInFromList(item)) {
            if (isMounted)
                setToList((prev) =>
                    prev.filter(
                        (p) => +p.relatedPat.related_pattern_id !== +item.relatedPat.related_pattern_id
                    )
                );
        }

        return () => {
            isMounted = false;
        };
    }, [existsInToList, existsInFromList, patternSelected]); // eslint-disable-line react-hooks/exhaustive-deps


    useImperativeHandle(ref, () => ({
        getList: () => {
            return toList.map(item => item.relatedPat);  // Return the current state of toList
        }
    }));

    return (
        <div className={styles.Container}>
            <div className={styles.SplitCols}>
                <h3>Related Patterns:</h3>
                <PulsingDivWrapper active={draggingFrom}>
                    <DroppableList
                        list={toList}
                        setList={setToList}
                        itemType={'RELATED_PATTERNS'}
                        targetListName="toList"
                        moveItemBetweenLists={moveItemBetweenLists}
                        noStyle
                        setDragging={setDraggingTo}
                    />
                </PulsingDivWrapper>
            </div>
            <div className={styles.Separator}></div>
            <div className={styles.SplitCols}>
                <h3>Related Patterns:</h3>
                <PulsingDivWrapper active={draggingTo}>
                    <DroppableList
                        list={fromList}
                        setList={setFromList}
                        itemType={'RELATED_PATTERNS'}
                        targetListName="fromList"
                        moveItemBetweenLists={moveItemBetweenLists}
                        noStyle
                        setDragging={setDraggingFrom}
                    />
                </PulsingDivWrapper>
            </div>
        </div>
    )
});

export default RelatedPatternMergingDisplay;
